import React from 'react';
import { CardProps } from './types';
import CardHeader from './CardHeader';
import CardContent from './CardContent';
import CardFooter from './CardFooter';
import CardSkeleton from './CardSkeleton';
import './Card.css';

// Define the compound component interface
interface CardComponent extends React.ForwardRefExoticComponent<CardProps & React.RefAttributes<HTMLDivElement>> {
  Header: typeof CardHeader;
  Content: typeof CardContent;
  Footer: typeof CardFooter;
  Skeleton: typeof CardSkeleton;
}

const CardBase = React.forwardRef<HTMLDivElement, CardProps>(({
  variant = 'default',
  size = 'medium',
  interactive = false,
  loading = false,
  className = '',
  children,
  onClick,
  elevation,
  sx = {},
}, ref) => {
  // If loading, show skeleton
  if (loading) {
    return (
      <CardSkeleton
        variant={variant}
        size={size}
        className={className}
        sx={sx}
      />
    );
  }

  const cardClasses = [
    'raith-card',
    `raith-card--${variant}`,
    `raith-card--${size}`,
    interactive || onClick ? 'raith-card--interactive' : '',
    className
  ].filter(Boolean).join(' ');

  const handleClick = () => {
    if (onClick && !loading) {
      onClick();
    }
  };

  const cardStyle = {
    ...sx,
    ...(elevation && {
      boxShadow: `0 ${elevation * 2}px ${elevation * 4}px rgba(0, 0, 0, 0.1)`
    })
  };

  return (
    <div
      ref={ref}
      className={cardClasses}
      style={cardStyle}
      onClick={handleClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      } : undefined}
    >
      {children}
    </div>
  );
});

CardBase.displayName = 'Card';

// Create the compound component
const Card = CardBase as CardComponent;

// Attach sub-components
Card.Header = CardHeader;
Card.Content = CardContent;
Card.Footer = CardFooter;
Card.Skeleton = CardSkeleton;

export default Card;